import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Users,
  Search,
  Loader2,
  Calendar,
  TrendingUp,
  Eye,
  Crown,
  Utensils,
  X
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMembers = () => {
  const { getAuthHeaders } = useAuth();
  const [members, setMembers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [dietDialogOpen, setDietDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberProgress, setMemberProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [togglingPremium, setTogglingPremium] = useState(null);
  const [selectedDietId, setSelectedDietId] = useState("");
  const [memberCurrentDiet, setMemberCurrentDiet] = useState(null);
  const [assigningDiet, setAssigningDiet] = useState(false);

  const [newMemberData, setNewMemberData] = useState({
    email: "",
    password: "",
    track: "",
  });

  const [editMemberData, setEditMemberData] = useState({
    program_id: "",
    program_start_date: "",
    track: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, programsRes, dietsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/members`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/programs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/diets`, { headers: getAuthHeaders() }),
      ]);
      setMembers(membersRes.data);
      setPrograms(programsRes.data);
      setDiets(dietsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberProgress = async (memberId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/members/${memberId}/progress`,
        { headers: getAuthHeaders() }
      );
      setMemberProgress(response.data);
      setProgressDialogOpen(true);
    } catch (error) {
      console.error("Error fetching progress:", error);
      toast.error("Error al cargar progreso");
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    
    if (!newMemberData.email || !newMemberData.password) {
      toast.error("Por favor completa email y contraseña");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/members`,
        newMemberData,
        { headers: getAuthHeaders() }
      );
      toast.success("Miembro creado exitosamente");
      fetchData();
      setDialogOpen(false);
      setNewMemberData({ email: "", password: "", track: "" });
    } catch (error) {
      console.error("Error creating member:", error);
      toast.error(error.response?.data?.detail || "Error al crear miembro");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setEditMemberData({
      program_id: member.profile?.program_id || "",
      program_start_date: member.profile?.program_start_date || "",
      track: member.profile?.track || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/members/${editingMember.id}`,
        editMemberData,
        { headers: getAuthHeaders() }
      );
      toast.success("Miembro actualizado");
      fetchData();
      setEditDialogOpen(false);
      setEditingMember(null);
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Error al actualizar miembro");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm("¿Estás seguro de eliminar este miembro?")) return;
    
    setDeleting(memberId);
    try {
      await axios.delete(`${API_URL}/api/admin/members/${memberId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Miembro eliminado");
      fetchData();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Error al eliminar miembro");
    } finally {
      setDeleting(null);
    }
  };

  const handleTogglePremium = async (member) => {
    setTogglingPremium(member.id);
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/members/${member.id}/premium`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Error toggling premium:", error);
      toast.error("Error al cambiar estado Premium");
    } finally {
      setTogglingPremium(null);
    }
  };

  const openDietDialog = async (member) => {
    setEditingMember(member);
    setSelectedDietId("");
    setMemberCurrentDiet(null);
    
    // Fetch current diet assignment
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/members/${member.id}/diet`,
        { headers: getAuthHeaders() }
      );
      if (response.data.diet) {
        setMemberCurrentDiet(response.data.diet);
        setSelectedDietId(response.data.diet.id);
      }
    } catch (error) {
      console.error("Error fetching member diet:", error);
    }
    
    setDietDialogOpen(true);
  };

  const handleAssignDiet = async () => {
    if (!selectedDietId) {
      toast.error("Selecciona una dieta");
      return;
    }

    setAssigningDiet(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/members/${editingMember.id}/assign-diet`,
        { diet_id: selectedDietId },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setDietDialogOpen(false);
      setEditingMember(null);
    } catch (error) {
      console.error("Error assigning diet:", error);
      toast.error(error.response?.data?.detail || "Error al asignar dieta");
    } finally {
      setAssigningDiet(false);
    }
  };

  const handleRemoveDiet = async () => {
    if (!window.confirm("¿Quitar la dieta asignada a este usuario?")) return;

    setAssigningDiet(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/members/${editingMember.id}/diet`,
        { headers: getAuthHeaders() }
      );
      toast.success("Dieta desasignada");
      setMemberCurrentDiet(null);
      setSelectedDietId("");
    } catch (error) {
      console.error("Error removing diet:", error);
      toast.error(error.response?.data?.detail || "Error al quitar dieta");
    } finally {
      setAssigningDiet(false);
    }
  };

  const filteredMembers = members.filter((m) =>
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-members">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Miembros</h1>
          <p className="text-muted-foreground mt-1">Gestiona los miembros del gimnasio</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" data-testid="add-member-button">
              <Plus className="w-5 h-5 mr-2" />
              Agregar Miembro
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-xl">
                Nuevo Miembro
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMember} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">Email *</Label>
                <Input
                  type="email"
                  value={newMemberData.email}
                  onChange={(e) => setNewMemberData({ ...newMemberData, email: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="miembro@email.com"
                  data-testid="new-member-email"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Contraseña *</Label>
                <Input
                  type="password"
                  value={newMemberData.password}
                  onChange={(e) => setNewMemberData({ ...newMemberData, password: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="••••••••"
                  data-testid="new-member-password"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Track</Label>
                <Select
                  value={newMemberData.track}
                  onValueChange={(value) => setNewMemberData({ ...newMemberData, track: value })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="new-member-track">
                    <SelectValue placeholder="Seleccionar (asigna programa automáticamente)" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="men" className="text-white hover:bg-white/10">
                      Hombres
                    </SelectItem>
                    <SelectItem value="women" className="text-white hover:bg-white/10">
                      Mujeres
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                  data-testid="create-member-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Crear Miembro"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar miembros..."
          className="pl-10 bg-secondary/50 border-transparent focus:border-primary text-white"
          data-testid="search-members-input"
        />
      </div>

      {/* Table */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No se encontraron miembros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Track</TableHead>
                    <TableHead className="text-muted-foreground">Estado</TableHead>
                    <TableHead className="text-muted-foreground">Programa</TableHead>
                    <TableHead className="text-muted-foreground">Entrenamientos</TableHead>
                    <TableHead className="text-muted-foreground text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => (
                    <TableRow
                      key={member.id}
                      className="border-white/5 hover:bg-[#1A1A1A]"
                      data-testid={`member-row-${member.id}`}
                    >
                      <TableCell className="font-medium text-white">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        {member.profile?.track ? (
                          <Badge 
                            variant="outline" 
                            className={cn(
                              member.profile.track === "men" 
                                ? "border-blue-500/50 text-blue-400" 
                                : "border-pink-500/50 text-pink-400"
                            )}
                          >
                            {member.profile.track === "men" ? "Hombres" : "Mujeres"}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            member.is_premium 
                              ? "border-[#D4AF37]/50 text-[#D4AF37] bg-[#D4AF37]/10" 
                              : "border-[#444] text-[#666]"
                          )}
                        >
                          {member.is_premium ? (
                            <><Crown className="w-3 h-3 mr-1" /> Premium</>
                          ) : (
                            "Free"
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[150px] truncate">
                        {member.program?.name || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-green-500/30 text-green-400">
                          {member.completed_workouts_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Toggle Premium */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleTogglePremium(member)}
                            disabled={togglingPremium === member.id}
                            className={cn(
                              "hover:bg-[#1A1A1A]",
                              member.is_premium && "text-[#D4AF37]"
                            )}
                            title={member.is_premium ? "Quitar Premium" : "Activar Premium"}
                            data-testid={`toggle-premium-${member.id}`}
                          >
                            {togglingPremium === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Crown className="w-4 h-4" />
                            )}
                          </Button>
                          {/* Assign Diet */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDietDialog(member)}
                            className={cn(
                              "hover:bg-[#1A1A1A]",
                              !member.is_premium && "opacity-50"
                            )}
                            disabled={!member.is_premium}
                            title={member.is_premium ? "Asignar Dieta" : "Solo para Premium"}
                            data-testid={`assign-diet-${member.id}`}
                          >
                            <Utensils className="w-4 h-4 text-green-400" />
                          </Button>
                          {/* View Progress */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fetchMemberProgress(member.id)}
                            className="hover:bg-[#1A1A1A]"
                            data-testid={`view-progress-${member.id}`}
                          >
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          {/* Edit */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(member)}
                            className="hover:bg-[#1A1A1A]"
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(member.id)}
                            disabled={deleting === member.id}
                            className="hover:bg-destructive/20"
                            data-testid={`delete-member-${member.id}`}
                          >
                            {deleting === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) setEditingMember(null);
      }}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              Editar Miembro
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMember} className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-[#1A1A1A]">
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="text-white font-medium">{editingMember?.email}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Track</Label>
              <Select
                value={editMemberData.track}
                onValueChange={(value) => setEditMemberData({ ...editMemberData, track: value })}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  <SelectItem value="men" className="text-white hover:bg-[#1A1A1A]">
                    Hombres
                  </SelectItem>
                  <SelectItem value="women" className="text-white hover:bg-[#1A1A1A]">
                    Mujeres
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Programa</Label>
              <Select
                value={editMemberData.program_id}
                onValueChange={(value) => setEditMemberData({ ...editMemberData, program_id: value })}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar programa" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id} className="text-white hover:bg-[#1A1A1A]">
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Fecha de Inicio</Label>
              <Input
                type="date"
                value={editMemberData.program_start_date}
                onChange={(e) => setEditMemberData({ ...editMemberData, program_start_date: e.target.value })}
                className="bg-secondary/50 border-transparent focus:border-primary text-white"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary hover:bg-primary/90"
                data-testid="update-member-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Actualizar"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diet Assignment Dialog */}
      <Dialog open={dietDialogOpen} onOpenChange={(open) => {
        setDietDialogOpen(open);
        if (!open) {
          setEditingMember(null);
          setMemberCurrentDiet(null);
          setSelectedDietId("");
        }
      }}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <Utensils className="w-5 h-5 text-green-400" />
              Asignar Dieta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-[#1A1A1A]">
              <p className="text-sm text-[#888]">Miembro</p>
              <p className="text-white font-medium">{editingMember?.email}</p>
            </div>

            {/* Current diet */}
            {memberCurrentDiet && (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-400 uppercase tracking-wider">Dieta actual</p>
                    <p className="text-white font-medium">{memberCurrentDiet.name}</p>
                    <p className="text-sm text-[#888]">{memberCurrentDiet.calories_target} kcal</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveDiet}
                    disabled={assigningDiet}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    title="Quitar dieta"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Diet selector */}
            <div className="space-y-2">
              <Label className="text-white">
                {memberCurrentDiet ? "Cambiar a otra dieta" : "Seleccionar dieta"}
              </Label>
              {diets.length === 0 ? (
                <div className="p-4 rounded-lg bg-[#1A1A1A] text-center">
                  <Utensils className="w-8 h-8 text-[#444] mx-auto mb-2" />
                  <p className="text-[#888] text-sm">No hay dietas creadas</p>
                  <p className="text-xs text-[#666] mt-1">Ve a la sección Dietas para crear una</p>
                </div>
              ) : (
                <Select
                  value={selectedDietId}
                  onValueChange={setSelectedDietId}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectValue placeholder="Seleccionar dieta" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    {diets.map((diet) => (
                      <SelectItem 
                        key={diet.id} 
                        value={diet.id} 
                        className="text-white hover:bg-[#1A1A1A]"
                      >
                        <div className="flex items-center gap-2">
                          <span>{diet.name}</span>
                          <span className="text-[#888] text-xs">({diet.calories_target} kcal)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDietDialogOpen(false)}
                className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAssignDiet}
                disabled={assigningDiet || !selectedDietId || diets.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-assign-diet"
              >
                {assigningDiet ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Asignando...
                  </>
                ) : (
                  "Asignar Dieta"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              Progreso del Miembro
            </DialogTitle>
          </DialogHeader>
          
          {memberProgress && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-secondary/30 text-center">
                  <p className="text-3xl font-heading font-bold text-green-400">
                    {memberProgress.total_workouts}
                  </p>
                  <p className="text-sm text-muted-foreground">Entrenamientos</p>
                </div>
                <div className="p-4 rounded-xl bg-secondary/30 text-center">
                  <p className="text-3xl font-heading font-bold text-primary">
                    {memberProgress.profile?.track === "men" ? "H" : memberProgress.profile?.track === "women" ? "M" : "-"}
                  </p>
                  <p className="text-sm text-muted-foreground">Track</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  Últimos entrenamientos
                </h4>
                {memberProgress.completed_workouts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Sin entrenamientos completados</p>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {memberProgress.completed_workouts.slice(0, 10).map((workout, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-[#1A1A1A]"
                      >
                        <span className="text-white">
                          {formatDate(workout.completed_at)}
                        </span>
                        <Badge variant="outline" className="border-green-500/30 text-green-400">
                          {workout.exercises_completed?.length || 0} ejercicios
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembers;
